import React, { useState, useEffect, useCallback } from 'react';
import { API, ax, btn } from './adminHelpers';
import { useSocket } from '../context/SocketContext';

export default function NotificationsTab({ setUnreadNotifications }) {
  const { socket } = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchNotifications = useCallback(async () => {
    setError('');
    try {
      const res = await ax().get(`${API}/api/notifications`);
      setNotifications(res.data.notifications || []);
      if (setUnreadNotifications) {
        setUnreadNotifications(res.data.unreadCount || 0);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch admin notifications.');
    } finally {
      setLoading(false);
    }
  }, [setUnreadNotifications]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!socket) return;
    const handle = () => {
      fetchNotifications();
    };
    socket.on('new_admin_notification', handle);
    return () => {
      socket.off('new_admin_notification', handle);
    };
  }, [socket, fetchNotifications]);

  const markRead = async (id) => {
    try {
      await ax().patch(`${API}/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      if (setUnreadNotifications) {
        setUnreadNotifications(prev => Math.max(0, prev - 1));
      }
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await ax().patch(`${API}/api/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      if (setUnreadNotifications) {
        setUnreadNotifications(0);
      }
    } catch (_) {}
  };

  return (
    <div style={{ padding: '4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: '#fff', fontWeight: 700 }}>🔔 Admin Notifications</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#a0a0b8' }}>
            System logs and alerts for bookings, contracts, and payments.
          </p>
        </div>
        {notifications.some(n => !n.read) && (
          <button onClick={markAllRead} style={{ ...btn('#9F5CFF', true), padding: '8px 16px' }}>
            ✓ Mark All as Read
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: 14, background: 'rgba(227,67,100,0.1)', border: '1px solid rgba(227,67,100,0.3)', borderRadius: 12, marginBottom: 20, color: '#E34364', fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#a0a0b8', fontSize: 14 }}>Loading alerts…</div>
      ) : notifications.length === 0 ? (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 40, margin: '0 0 12px' }}>🔔</p>
          <p style={{ color: '#a0a0b8', fontSize: 14, fontWeight: 600, margin: 0 }}>No notifications yet.</p>
          <p style={{ color: '#555', fontSize: 12, margin: '6px 0 0' }}>Real-time updates will show up here when actions are taken by customers.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notifications.map(n => {
            const isRead = n.read;
            return (
              <div 
                key={n.id} 
                onClick={() => !isRead && markRead(n.id)}
                style={{ 
                  background: isRead ? 'rgba(255,255,255,0.02)' : 'rgba(159,92,255,0.06)', 
                  border: `1px solid ${isRead ? 'rgba(255,255,255,0.06)' : 'rgba(159,92,255,0.2)'}`, 
                  borderRadius: 12, 
                  padding: '16px 20px', 
                  cursor: isRead ? 'default' : 'pointer', 
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <p style={{ margin: 0, fontSize: 14, color: isRead ? '#e0e0e0' : '#fff', fontWeight: isRead ? 400 : 600, lineHeight: 1.5 }}>
                    {n.message}
                  </p>
                  {!isRead && (
                    <span style={{ 
                      flexShrink: 0, 
                      background: '#9F5CFF', 
                      color: '#fff', 
                      fontSize: 10, 
                      fontWeight: 700, 
                      padding: '2px 8px', 
                      borderRadius: 50,
                      boxShadow: '0 0 10px rgba(159,92,255,0.4)'
                    }}>
                      New
                    </span>
                  )}
                </div>
                <p style={{ margin: '8px 0 0', fontSize: 11, color: '#6b7080' }}>
                  {new Date(n.created_at).toLocaleString()}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
